import React, { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { BRAND } from "../config/brand";
import { useI18n } from "../contexts/I18nContext";
import { SearchSuggestionsPanel, useSearchSuggestions } from "./SearchSuggestions";
import AppIcon from "./AppIcon";

export default function AppNavbar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef(null);
  const toggleRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { lang, setLang, t, LANG_OPTIONS } = useI18n();
  const suggestions = useSearchSuggestions(query, open && suggestionsOpen, { includeRental: true, limit: 7 });

  useEffect(() => { setOpen(false); setSuggestionsOpen(false); setQuery(""); }, [location.pathname, location.search]);
  useEffect(() => { setActiveIndex(-1); }, [query]);
  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const submit = (value = query) => {
    const q = value.trim();
    if (q) { setOpen(false); navigate(`/store?q=${encodeURIComponent(q)}`); }
  };
  const pickProduct = (product) => {
    if (product?.slug) { setOpen(false); navigate(`/p/${product.slug}`); }
  };
  const close = () => { setOpen(false); toggleRef.current?.focus(); };
  const keyDown = (event) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) return;
    const products = suggestions.products || [];
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      setSuggestionsOpen(true);
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveIndex((index) => products.length ? (index + step + products.length) % products.length : -1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (suggestionsOpen && activeIndex >= 0 && products[activeIndex]) pickProduct(products[activeIndex]);
      else submit();
    }
  };
  const links = [["/rental", t.rental || "렌탈"], ["/store", t.store || "스토어"], ["/store/toner-supplies", "토너 · 소모품"], ["/consultation", t.inquiry || "상담"]];

  return <header className={`clone-app-header${open ? " is-open" : ""}`} onKeyDown={(event) => { if (event.key === "Escape" && open) close(); }}>
    <div className="clone-app-toolbar">
      <Link to="/" className="clone-app-wordmark" aria-label={`${BRAND.name} 홈`}>{BRAND.logo}<span>복합기 · 렌탈 · 오피스</span></Link>
      <div className="clone-app-header-actions">
        {BRAND.kakaoUrl && <a href={BRAND.kakaoUrl} target="_blank" rel="noopener noreferrer" aria-label="카카오톡 문의하기"><AppIcon name="chat" size={22} /></a>}
        <button ref={toggleRef} type="button" aria-label={open ? "검색 및 메뉴 닫기" : "검색 및 메뉴 열기"} aria-expanded={open} aria-controls="clone-app-menu" onClick={() => setOpen(!open)}>
          {open ? <span aria-hidden="true" className="clone-close-icon">×</span> : <AppIcon name="search" size={23} />}
        </button>
      </div>
    </div>
    <nav className="clone-app-tabs" aria-label="주요 서비스">
      {links.map(([to, label]) => <NavLink key={to} to={to} end={to === "/store"}>{label}</NavLink>)}
    </nav>
    {open && <div id="clone-app-menu" className="clone-app-menu">
      <form role="search" onSubmit={(event) => { event.preventDefault(); submit(); }}>
        <label htmlFor="clone-header-search">제품·모델명으로 검색</label>
        <div className="clone-search-field">
          <input id="clone-header-search" ref={inputRef} value={query} onChange={(event) => setQuery(event.target.value)} onFocus={() => setSuggestionsOpen(true)} onKeyDown={keyDown} placeholder="제품명 또는 모델명" autoComplete="off" enterKeyHint="search" />
          <button type="submit" aria-label="제품 검색"><AppIcon name="search" size={22} /></button>
        </div>
        {suggestionsOpen && <SearchSuggestionsPanel query={query} suggestions={suggestions} activeIndex={activeIndex} onPickProduct={pickProduct} onPickKeyword={(keyword) => { setQuery(keyword); inputRef.current?.focus(); }} onSubmit={() => submit()} style={{ position: "relative", top: "auto", left: 0, width: "100%", maxHeight: "40dvh", marginTop: 12 }} />}
      </form>
      <div className="clone-menu-links">
        <Link to="/compare"><AppIcon name="compare" size={20} />제품 비교</Link>
        <Link to="/consultation?tab=general&type=as"><AppIcon name="service" size={20} />A/S 상담</Link>
        <a href={`tel:${BRAND.phone}`}><AppIcon name="phone" size={20} />{BRAND.phone}</a>
        <Link to="/sitemap"><AppIcon name="store" size={20} />전체 서비스</Link>
      </div>
      <label className="clone-language">언어 / Language
        <select value={lang} onChange={(event) => setLang(event.target.value)}>{(LANG_OPTIONS || []).map((item) => <option key={item.code} value={item.code}>{item.label}</option>)}</select>
      </label>
    </div>}
  </header>;
}
